import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, BusFront, CarFront, LoaderCircle, Luggage, Navigation, Search, UsersRound } from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoogleMapView from "../../components/GoogleMapView";
import { CITIES, getTripQuery, VEHICLE_CLASSES, VEHICLES } from "../../data/ticketing";
import { loadPublicVehicleCatalogCount } from "../../data/vehicleCatalog";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import CityPicker from "../../components/CityPicker";
import DatePicker from "../../components/DatePicker";

const inputBaseClass = "h-14 w-full rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 shadow-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500";
const inputClass = `${inputBaseClass} px-4`;
const iconInputClass = `${inputBaseClass} pl-11`;
const REQUEST_DRAFT_KEY = "uztravel:ticket-request-draft:v1";
const REQUEST_DRAFT_TTL_MS = 12 * 60 * 60 * 1000;

const CITY_COORDINATES = {
  tashkent: [41.2995, 69.2401], samarkand: [39.6542, 66.9597], bukhara: [39.7681, 64.4556],
  khiva: [41.3783, 60.3639], urgench: [41.55, 60.63], nukus: [42.46, 59.61],
  andijan: [40.78, 72.34], namangan: [41, 71.67], fergana: [40.39, 71.78],
  jizzakh: [40.12, 67.84], qarshi: [38.86, 65.8], termez: [37.22, 67.28],
  navoi: [40.1, 65.37], gulistan: [40.49, 68.78], shahrisabz: [39.06, 66.83],
};

function findNearestCity(latitude, longitude) {
  let nearest = null;
  for (const [slug, [cityLatitude, cityLongitude]] of Object.entries(CITY_COORDINATES)) {
    const radians = (degrees) => (degrees * Math.PI) / 180;
    const latitudeDelta = radians(cityLatitude - latitude);
    const longitudeDelta = radians(cityLongitude - longitude);
    const haversine = Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(radians(latitude)) * Math.cos(radians(cityLatitude)) * Math.sin(longitudeDelta / 2) ** 2;
    const distanceKm = 6371 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    if (!nearest || distanceKm < nearest.distanceKm) nearest = { slug, distanceKm };
  }
  return nearest;
}

function getCurrentCoordinates() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("location_unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
      () => reject(new Error("location_denied")),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  });
}

function BookingMap({ city, pickupCoords, focusRequestId, t }) {
  const pickupPosition = pickupCoords ? [pickupCoords.lat, pickupCoords.lng] : null;
  const center = pickupPosition || CITY_COORDINATES[city] || CITY_COORDINATES.tashkent;

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#e8eee8] dark:bg-slate-950">
      <GoogleMapView
        center={center}
        zoom={pickupPosition ? 15 : 12}
        scrollWheelZoom
        className="absolute inset-0 h-full w-full"
        ariaLabel={t("rides.open_map")}
        markers={pickupPosition ? [{ position: pickupPosition, label: t("rides.pickup"), person: true }] : []}
        focusPosition={pickupPosition}
        focusRequestId={focusRequestId}
      />
    </div>
  );
}

function createRequestKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getTashkentDateTimeParts(date = new Date()) {
  return Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).map(({ type, value }) => [type, value]));
}

function getEarliestTashkentSelection(now = new Date()) {
  const parts = getTashkentDateTimeParts(now);
  // A time input has minute precision. The current minute may already be
  // underway, so round up rather than offering a value that is already past.
  const nextMinute = new Date(Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute) + 1,
  ));
  const value = nextMinute.toISOString();
  return { date: value.slice(0, 10), time: value.slice(11, 16) };
}

function readRequestDraft(search) {
  try {
    const draft = JSON.parse(window.sessionStorage.getItem(REQUEST_DRAFT_KEY) || "null");
    if (
      !draft
      || draft.search !== search
      || !Number.isFinite(draft.savedAt)
      || Date.now() - draft.savedAt > REQUEST_DRAFT_TTL_MS
      || Date.now() < draft.savedAt
    ) return null;

    const savedForm = draft.form && typeof draft.form === "object" ? draft.form : {};
    return {
      routeForm: {
        from: "",
        to: CITIES.includes(draft.routeForm?.to) ? draft.routeForm.to : "",
      },
      transportType: ["car", "bus"].includes(draft.transportType) ? draft.transportType : "car",
      selectedClass: VEHICLE_CLASSES.includes(draft.selectedClass) ? draft.selectedClass : "standard",
      travelDate: /^\d{4}-\d{2}-\d{2}$/.test(draft.travelDate || "") ? draft.travelDate : "",
      form: {
        passengers: /^\d{1,2}$/.test(savedForm.passengers || "") ? savedForm.passengers : "1",
        luggage: /^\d{1,2}$/.test(savedForm.luggage || "") ? savedForm.luggage : "0",
        note: typeof savedForm.note === "string" ? savedForm.note.slice(0, 1500) : "",
      },
    };
  } catch {
    return null;
  }
}

function getTashkentDeparture(date, time) {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  // Uzbekistan uses UTC+05:00. Build the instant from that wall-clock time
  // explicitly, rather than interpreting it in the passenger's device zone.
  return new Date(Date.UTC(year, month - 1, day, hour - 5, minute));
}

function getErrorKey(error) {
  const message = String(error?.message || "");
  if (message.includes("duplicate_active_ride_request")) return "duplicate_request";
  if (message.includes("no_available_driver")) return "no_available_drivers";
  if (message.includes("vehicle_no_longer_available")) return "vehicle_unavailable";
  if (message.includes("travel_time_must_be_future")) return "time_invalid";
  if (message.includes("passenger_capacity_exceeded")) return "passenger_limit";
  if (message.includes("luggage_capacity_exceeded")) return "luggage_limit";
  if (message.includes("invalid_client_phone")) return "phone_invalid";
  if (message.includes("client_account_required")) return "client_role_required";
  if (["42P01", "PGRST202", "PGRST203", "PGRST205", "42883"].includes(error?.code)
    || /could not find the function|does not exist|schema cache/i.test(message)) return "setup_error";
  return "submit_error";
}

export default function TicketDriverPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTrip = getTripQuery(params);
  const [initialDraft] = useState(() => readRequestDraft(location.search));
  const lastAppliedSearch = useRef(location.search);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const earliestSelection = getEarliestTashkentSelection(new Date(clockNow));
  const [routeForm, setRouteForm] = useState(() => ({
    from: "",
    to: initialDraft?.routeForm.to || (CITIES.includes(params.get("to")) ? params.get("to") : ""),
  }));
  const [transportType, setTransportType] = useState(() => initialDraft?.transportType || (["car", "bus"].includes(params.get("type")) ? params.get("type") : "car"));
  const [selectedClass, setSelectedClass] = useState(() => initialDraft?.selectedClass || (VEHICLE_CLASSES.includes(params.get("class")) ? params.get("class") : "standard"));
  const initialDate = params.get("date");
  const [travelDate, setTravelDate] = useState(() => {
    const minimum = getEarliestTashkentSelection().date;
    if (initialDraft?.travelDate && initialDraft.travelDate >= minimum) return initialDraft.travelDate;
    return initialTrip.valid && initialDate >= minimum ? initialDate : minimum;
  });
  const [hasMatchingVehicles, setHasMatchingVehicles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadCatalog, setReloadCatalog] = useState(0);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [pickupFocusRequest, setPickupFocusRequest] = useState(0);
  const [locating, setLocating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const requestKey = useRef(createRequestKey());
  const [form, setForm] = useState(() => initialDraft?.form || { passengers: "1", luggage: "0", note: "" });

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (location.search === lastAppliedSearch.current) return;
    lastAppliedSearch.current = location.search;
    const incomingTrip = getTripQuery(params);
    if (incomingTrip.valid) {
      setRouteForm({ from: "", to: incomingTrip.to });
      setTravelDate(incomingTrip.date < getEarliestTashkentSelection().date ? getEarliestTashkentSelection().date : incomingTrip.date);
      setPickupCoords(null);
      setFormError("");
    }
    const incomingType = params.get("type");
    const incomingClass = params.get("class");
    if (["car", "bus"].includes(incomingType)) setTransportType(incomingType);
    if (VEHICLE_CLASSES.includes(incomingClass)) setSelectedClass(incomingClass);
    // Apply changed query parameters only. On initial mount, an in-progress
    // draft for this exact URL takes precedence so a refresh won't reset it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(REQUEST_DRAFT_KEY, JSON.stringify({
        search: location.search,
        savedAt: Date.now(),
        routeForm,
        transportType,
        selectedClass,
        travelDate,
        form,
      }));
    } catch (error) {
      console.warn("Could not save the ride request draft for this tab", error);
    }
  }, [location.search, routeForm, transportType, selectedClass, travelDate, form]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    loadPublicVehicleCatalogCount(supabase, transportType, selectedClass)
      .then((count) => {
        if (active) {
          setHasMatchingVehicles(count > 0);
        }
      })
      .catch((error) => {
        console.error("Could not load approved vehicle listing", error);
        if (active) setLoadError(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [transportType, selectedClass, reloadCatalog]);

  const returnPath = `${location.pathname}${location.search}`;
  const selectedVehicle = VEHICLES.find((vehicle) => vehicle.transportType === transportType && vehicle.class === selectedClass);
  const passengerMax = selectedVehicle?.capacity || (transportType === "car" ? 4 : 50);
  const updateRoute = (field, value) => {
    setRouteForm((current) => ({ ...current, [field]: value }));
    setFormError("");
  };

  const locateMe = useCallback(() => {
    setFormError("");
    setPickupCoords(null);
    setRouteForm((current) => ({ ...current, from: "" }));
    if (!navigator.geolocation) {
      setFormError("location_unavailable");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const nearestCity = findNearestCity(coords.latitude, coords.longitude);
        if (!nearestCity || nearestCity.distanceKm > 80) {
          setFormError("location_city_not_found");
          setLocating(false);
          return;
        }
        setPickupCoords({ lat: coords.latitude, lng: coords.longitude });
        setPickupFocusRequest((current) => current + 1);
        setRouteForm((current) => ({ ...current, from: nearestCity.slug }));
        setLocating(false);
      },
      () => {
        setFormError("location_denied");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    locateMe();
  }, [locateMe, location.search]);

  const submitRequest = async (event) => {
    event.preventDefault();
    setFormError("");
    if (!routeForm.from || !routeForm.to) {
      setFormError("route_required");
      return;
    }
    if (routeForm.from === routeForm.to) {
      setFormError("route_same");
      return;
    }
    if (!travelDate) {
      setFormError("date_required");
      return;
    }
    if (!user) {
      setFormError("auth_required");
      return;
    }
    if (!user.canTravel) {
      setFormError("client_role_required");
      return;
    }
    if (loadError) {
      setFormError("catalog_unavailable");
      return;
    }
    if (!hasMatchingVehicles) {
      setFormError("no_available_drivers");
      return;
    }
    setLocating(true);
    let currentPickupCoords;
    try {
      currentPickupCoords = await getCurrentCoordinates();
    } catch (error) {
      setFormError(error.message === "location_unavailable" ? "location_unavailable" : "location_denied");
      setLocating(false);
      return;
    }
    setLocating(false);
    const detectedOrigin = findNearestCity(currentPickupCoords.lat, currentPickupCoords.lng);
    if (!detectedOrigin || detectedOrigin.distanceKm > 80) {
      setPickupCoords(null);
      setRouteForm((current) => ({ ...current, from: "" }));
      setFormError("location_city_not_found");
      return;
    }
    setPickupCoords(currentPickupCoords);
    setPickupFocusRequest((current) => current + 1);
    if (detectedOrigin.slug !== routeForm.from) {
      setRouteForm((current) => ({ ...current, from: detectedOrigin.slug }));
      setFormError("location_city_changed");
      return;
    }
    const departureTime = travelDate === earliestSelection.date ? earliestSelection.time : "09:00";
    const departure = getTashkentDeparture(travelDate, departureTime);
    if (Number.isNaN(departure.valueOf()) || departure <= new Date()) {
      setFormError("time_invalid");
      return;
    }
    setSubmitting(true);
    try {
      const { data: requestId, error } = await supabase.rpc("create_broadcast_ride_request", {
        p_client_request_key: requestKey.current,
        p_transport_type: transportType,
        p_vehicle_class: selectedClass,
        p_from_city: routeForm.from,
        p_to_city: routeForm.to,
        p_travel_at: departure.toISOString(),
        p_pickup_address: t("rides.current_location_label"),
        p_pickup_lat: currentPickupCoords.lat,
        p_pickup_lng: currentPickupCoords.lng,
        p_passenger_count: Number(form.passengers),
        p_luggage_count: Number(form.luggage),
        p_client_phone: user.phone || user.user_metadata?.phone || null,
        p_note: form.note.trim() || null,
      });
      if (error) throw error;
      try {
        window.sessionStorage.removeItem(REQUEST_DRAFT_KEY);
      } catch {
        // A blocked sessionStorage must not turn a successfully created ride into an error.
      }
      requestKey.current = createRequestKey();
      navigate(`/tickets/searching?request=${encodeURIComponent(requestId)}`, { replace: true });
    } catch (error) {
      console.error("Could not create vehicle trip request", error);
      setFormError(getErrorKey(error));
    } finally {
      setSubmitting(false);
    }
  };

  const errorMessage = {
    route_required: t("tickets.validation_required"),
    route_same: t("tickets.validation_same"),
    date_required: t("tickets.validation_date"),
    auth_required: t("rides.sign_in_text"),
    catalog_unavailable: t("tickets.flow.catalog_error"),
  }[formError] || (formError ? t(`rides.${formError}`, t("rides.submit_error")) : "");

  return (
    <main className="fixed inset-0 z-[200] overflow-hidden bg-[#e8eee8] text-slate-950 transition-colors dark:bg-slate-950 dark:text-slate-100">
      <BookingMap city={routeForm.from} pickupCoords={pickupCoords} focusRequestId={pickupFocusRequest} t={t} />
      <div className="pointer-events-none absolute inset-0 z-[400] bg-white/5" aria-hidden="true" />
      <section className="absolute inset-x-2 bottom-2 top-2 z-[700] flex min-h-0 flex-col overflow-hidden rounded-[1.6rem] bg-white shadow-[0_16px_60px_rgba(15,23,42,.28)] transition-colors dark:bg-slate-900 dark:shadow-black/50 sm:inset-y-5 sm:left-5 sm:right-auto sm:w-[min(540px,calc(100vw-2.5rem))]">
        <header className="shrink-0 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-5">
          <div className="mb-3 flex items-center gap-3">
            <Link to="/" aria-label={t("tickets.return_home")} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"><ArrowLeft className="h-4 w-4" /></Link>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full text-sm font-black text-slate-900 dark:text-slate-100"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400 text-slate-950"><BusFront className="h-5 w-5" /></span>UzTravell</Link>
          </div>
          <h1 className="text-xl font-black tracking-tight sm:text-2xl">{t("rides.booking_title")}</h1>
          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{t("rides.booking_subtitle")}</p>
        </header>

        <form onSubmit={submitRequest} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">
          <section aria-label={t("tickets.flow.route")} className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[10px] font-extrabold uppercase tracking-[.16em] text-slate-500 dark:text-slate-400">{t("tickets.flow.route")}</p>
            </div>
            <div className="grid grid-cols-[16px_minmax(0,1fr)] gap-x-2">
              <span className="mt-[50px] h-3 w-3 justify-self-center rounded-full border-[3px] border-orange-500" />
              <div className="min-w-0">
                <div className="mb-2 flex h-5 items-center justify-between gap-2">
                  <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">{t("tickets.from")}</span>
                  <button type="button" onClick={locateMe} disabled={locating} className="inline-flex shrink-0 items-center gap-1 text-[10px] font-extrabold text-amber-700 transition hover:text-amber-900 disabled:cursor-wait disabled:opacity-60 dark:text-amber-300 dark:hover:text-amber-200">
                    <Navigation className={`h-3 w-3 ${locating ? "animate-pulse" : ""}`} />
                    {locating ? t("rides.locating_short") : t(pickupCoords ? "rides.location_added" : "rides.use_my_location")}
                  </button>
                </div>
                <CityPicker ariaLabel={t("tickets.from")} placeholder={t("rides.current_location_label")} value={routeForm.from} onChange={(value) => updateRoute("from", value)} disabled />
              </div>

              <div className="flex h-4 justify-center" aria-hidden="true">
                <span className="h-full border-l-2 border-dashed border-slate-300 dark:border-slate-700" />
              </div>
              <div className="h-4" aria-hidden="true" />

              <span className="mt-[50px] h-3 w-3 justify-self-center rounded-sm border-[3px] border-slate-700 dark:border-slate-400" />
              <div className="min-w-0">
                <span className="mb-2 flex h-5 items-center text-[10px] font-bold text-slate-500 dark:text-slate-400">{t("tickets.to")}</span>
                <CityPicker ariaLabel={t("tickets.to")} placeholder={t("tickets.choose_city")} value={routeForm.to} onChange={(value) => updateRoute("to", value)} />
              </div>

              <div className="col-span-2 mt-3" aria-hidden="true" />
              <span aria-hidden="true" />
              <label className="block min-w-0">
                <span className="mb-2 flex h-5 items-center text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("tickets.date")}</span>
                <DatePicker ariaLabel={t("tickets.date")} placeholder={t("tickets.date")} minDate={earliestSelection.date} value={travelDate} onChange={(value) => { setTravelDate(value); setFormError(""); }} />
              </label>
            </div>
          </section>

          <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-2 flex items-end justify-between gap-2"><div><p className="text-[10px] font-extrabold uppercase tracking-[.16em] text-slate-500 dark:text-slate-400">{t("tickets.flow.your_transport")}</p><h2 className="mt-0.5 text-sm font-black">{t("tickets.flow.choose_class")}</h2></div>{loading && <span role="status" className="text-[10px] font-semibold text-slate-400 dark:text-slate-500">{t("tickets.flow.catalog_loading")}</span>}</div>
            <div className="grid grid-cols-2 gap-3">
              {[{ id: "car", Icon: CarFront }, { id: "bus", Icon: BusFront }].map(({ id, Icon }) => {
                const copy = t(`tickets.flow.transport_types.${id}`, { returnObjects: true });
                const active = transportType === id;
                return <button key={id} type="button" aria-pressed={active} onClick={() => { setTransportType(id); setFormError(""); }} className={`flex min-h-11 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-extrabold transition ${active ? "border-amber-400 bg-amber-50 text-slate-950 ring-1 ring-amber-300 dark:bg-amber-400/10 dark:text-slate-100 dark:ring-amber-400/50" : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-amber-400/50"}`}><Icon className="h-4 w-4 text-amber-500" />{copy.title}</button>;
              })}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {VEHICLE_CLASSES.map((vehicleClass) => {
                const Icon = transportType === "bus" ? BusFront : CarFront;
                const copy = t(`tickets.flow.classes.${vehicleClass}`, { returnObjects: true });
                const active = selectedClass === vehicleClass;
                const optionVehicle = VEHICLES.find((vehicle) => vehicle.transportType === transportType && vehicle.class === vehicleClass);
                const capacity = optionVehicle?.capacityLabelKey
                  ? t(`tickets.flow.${optionVehicle.capacityLabelKey}`, { count: optionVehicle.capacity })
                  : optionVehicle ? t("tickets.flow.up_to_passengers", { count: optionVehicle.capacity }) : copy.tag;
                return <button key={vehicleClass} type="button" aria-pressed={active} onClick={() => { setSelectedClass(vehicleClass); setFormError(""); }} className={`min-h-[124px] rounded-2xl border-2 p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 ${active ? "border-amber-400 bg-amber-50 dark:bg-amber-400/10" : "border-slate-200 bg-white hover:border-amber-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-amber-400/50"}`}><Icon className={`h-5 w-5 ${active ? "text-amber-600 dark:text-amber-300" : "text-slate-400 dark:text-slate-500"}`} /><span className="mt-3 block text-sm font-extrabold leading-5 text-slate-900 dark:text-slate-100">{copy.title}</span><span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">{capacity || copy.tag}</span></button>;
              })}
            </div>
            {loadError ? <div role="alert" className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-400/10 dark:text-red-200"><span>{t("tickets.flow.catalog_error")}</span><button type="button" onClick={() => setReloadCatalog((attempt) => attempt + 1)} className="shrink-0 font-extrabold underline">{t("tickets.flow.catalog_retry")}</button></div> : !loading && !hasMatchingVehicles ? <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950 dark:bg-amber-400/10 dark:text-amber-100">{t("tickets.flow.no_approved_vehicles")}</p> : null}
          </section>

          <section className="mt-4 grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900 sm:grid-cols-2">
            <label className="block min-w-0"><span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("rides.passenger_count_label")}</span><span className="relative block"><UsersRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-500" /><input type="number" required min="1" max={passengerMax} value={form.passengers} onChange={(event) => setForm((current) => ({ ...current, passengers: event.target.value }))} className={iconInputClass} /></span></label>
            <label className="block min-w-0"><span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("rides.luggage_count_label")}</span><span className="relative block"><Luggage className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-500" /><input type="number" required min="0" max="50" value={form.luggage} onChange={(event) => setForm((current) => ({ ...current, luggage: event.target.value }))} className={iconInputClass} /></span></label>
            <label className="block sm:col-span-2"><span className="mb-2 block text-xs font-extrabold">{t("rides.note_label")}</span><textarea rows={3} maxLength={1500} value={form.note} onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))} placeholder={t("rides.note_placeholder")} className={`${inputClass} h-auto min-h-28 resize-y py-3`} /></label>
          </section>

          <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex min-h-14 items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 text-sm dark:bg-slate-800"><span className="font-semibold">{t("rides.payment_method", "Способ оплаты")}</span><span className="text-right font-bold text-slate-600 dark:text-slate-300">{t("rides.payment_cash", "Наличные водителю")}</span></div>
            <p className="mt-2 text-[10px] leading-4 text-slate-500 dark:text-slate-400">{t("rides.payment_notice")}</p>
          </section>

          {formError && <div role="alert" className={`mt-4 rounded-xl border px-3 py-2 text-xs leading-5 ${formError === "duplicate_request" ? "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100" : "border-red-200 bg-red-50 text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200"}`}><p>{errorMessage}</p>{formError === "auth_required" && <div className="mt-2 flex gap-3"><Link to="/login" state={{ from: returnPath }} className="font-extrabold underline">{t("rides.login")}</Link><Link to="/register" state={{ from: returnPath }} className="font-extrabold underline">{t("rides.register")}</Link></div>}{formError === "duplicate_request" && <Link to="/dashboard" className="mt-1 inline-flex font-extrabold underline">{t("rides.my_requests")}</Link>}</div>}
          {user && !user.canTravel && <p className="mt-4 rounded-xl bg-amber-50 p-3 text-xs leading-5 text-amber-950 dark:bg-amber-400/10 dark:text-amber-100">{t("rides.client_role_text")} <Link to="/dashboard" className="font-extrabold underline">{t("rides.my_requests")}</Link></p>}
          <button type="submit" disabled={submitting || locating || loading || (!hasMatchingVehicles && !loadError)} className="mt-4 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#ffdf00] px-5 text-sm font-black text-slate-950 transition hover:bg-[#f4d400] disabled:cursor-not-allowed disabled:opacity-60">{submitting || locating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}{t(submitting ? "rides.sending" : locating ? "rides.locating" : "rides.send_request")}</button>
        </form>
      </section>
    </main>
  );
}
